import React, { useEffect, useState } from "react";
import { Checkbox, Form, Input, Modal, Select } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClose, faMinus, faPlus } from "@fortawesome/free-solid-svg-icons";
import { CaretDownFilled } from "@ant-design/icons";
import { useMutation, useQuery } from "@apollo/client";
import { useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import Spinner from "@src/components/spinner";
import { setNotification } from "@src/middleware/redux/reducers/notification.reducer";
import { getAllFormQuery } from "@src/util/query/allForm.query";
import { newSiteCheckpointMutation, updateSiteCheckpointMutation } from "@src/util/mutation/siteCheckpoint.mutation";
import { getSiteCheckpointByIdQuery } from "@src/util/query/siteCheckpoint.query";
import "@src/components/createTask/createFieldDrawer.css";


const FIELD_TYPES = [
    { value: "singlelineText", label: "Single-line text" },
    { value: "multilineText", label: "Multi-line text" },
    { value: "number", label: "Number" },
    { value: "date", label: "Date picker" },
    { value: "time", label: "Time picker" },
    { value: "singleCheckbox", label: "Single checkbox" },
    { value: "selectDropdown", label: "Dropdown select" },
    { value: "file", label: "File upload" },
    { value: "signature", label: "Signature" },
    { value: "location", label: "Location" },
];

export const CheckpointDrawer = ({ visible, close, refetch, editId = null, siteId = null }) => {
    // Site Detail's own checkpoints tab is routed with the site id in the URL and never
    // passes siteId explicitly; callers outside that route (e.g. the schedule module's
    // task/checkpoint picker) pass it directly since there's no matching route param here.
    const param = useParams();
    const resolvedSiteId = siteId || param?.id;
    const dispatch = useDispatch();

    const [name, setName] = useState("");
    const [tagType, setTagType] = useState(null);
    const [responseFlow, setResponseFlow] = useState("form");
    const [formId, setFormId] = useState(null);
    const [fields, setFields] = useState([{ label: "", fieldType: "" }]);

    const [newSiteCheckpoint, { loading: createLoading }] = useMutation(newSiteCheckpointMutation);
    const [updateSiteCheckpoint, { loading: updateLoading }] = useMutation(updateSiteCheckpointMutation);

    const { data: getAllFormData, loading: getAllFormDataLoading } = useQuery(getAllFormQuery, {
        fetchPolicy: "network-only",
    });

    const { data: checkpointToEdit, loading: editLoading } = useQuery(getSiteCheckpointByIdQuery, {
        variables: { checkpointId: editId },
        skip: !editId,
        fetchPolicy: "network-only",
    });

    useEffect(() => {
        if (checkpointToEdit?.getSiteCheckpointById?.response) {
            const cp = checkpointToEdit.getSiteCheckpointById.response;
            setName(cp.name || "");
            setTagType(cp.tagType || null);
            setResponseFlow(cp.responseFlow || null);
            setFormId(cp.formId || null);
            setFields(cp.fields?.length > 0 ? cp.fields : [{ label: "", fieldType: "" }]);
        }
    }, [checkpointToEdit?.getSiteCheckpointById?.response]);

    const resetForm = () => {
        setName("");
        setTagType(null);
        setResponseFlow(null);
        setFormId(null);
        setFields([{ label: "", fieldType: "" }]);
    };

    const handleClose = () => {
        resetForm();
        close();
    };

    const isDisabled =
        !name.trim() ||
        !tagType ||
        !responseFlow ||
        (responseFlow === "form" && !formId) ||
        (responseFlow === "fields" && !fields.every((f) => f.fieldType?.length > 0));

    const handleSubmit = async () => {
        try {
            const input = {
                siteId: resolvedSiteId,
                name: name.trim(),
                tagType,
                responseFlow,
                formId: responseFlow === "form" ? formId : null,
                fields: responseFlow === "fields" ? fields.map((f) => ({ label: f.label, fieldType: f.fieldType })) : [],
            };

            if (editId) {
                await updateSiteCheckpoint({ variables: { input: { ...input, _id: editId } } });
            } else {
                await newSiteCheckpoint({ variables: { input } });
            }

            dispatch(setNotification({
                error: false,
                notificationState: true,
                message: editId ? "Checkpoint updated successfully" : "Checkpoint created successfully",
            }));

            await refetch();
            handleClose();
        } catch (err) {
            dispatch(setNotification({
                error: true,
                notificationState: true,
                message: err.message,
            }));
        }
    };

    const addField = () => setFields([...fields, { label: "", fieldType: "" }]);

    const removeField = (index) => setFields(fields.filter((_, i) => i !== index));

    const updateField = (index, key, value) => {
        setFields(fields.map((f, i) => (i === index ? { ...f, [key]: value } : f)));
    };

    const loading = createLoading || updateLoading;

    return (
        <Modal
            closable={false}
            open={visible}
            onClose={handleClose}
            width={700}
            mask={true}
            maskClosable={false}
            maskClassName="filtermask"
            style={{ top: "20px" }}
            footer={
                <div className="drawer-footer" style={{ display: "flex" }}>
                    <button
                        onClick={handleSubmit}
                        disabled={isDisabled || loading}
                        className={isDisabled || loading ? "disabled-btn drawer-filled-btn" : "drawer-filled-btn"}
                    >
                        {loading ? <Spinner /> : editId ? "Update" : "Create"}
                    </button>
                    <button
                        disabled={loading}
                        className="drawer-outlined-btn"
                        onClick={handleClose}
                    >
                        Cancel
                    </button>
                </div>
            }
        >
            <React.Fragment>
                <div className="modal-header-title">
                    <span>{editId ? "Edit Checkpoint" : "Create Checkpoint"}</span>
                    <span onClick={handleClose}>
                        <FontAwesomeIcon className="close" icon={faClose} />
                    </span>
                </div>

                <div className="modal-body" >
                    {editId && editLoading ? (
                        <div style={{display:'flex', justifyContent:'center', alignItems:'center'}}>
                            <Spinner />
                        </div>
                    ) : (
                        <Form style={{ display: "flex", gap: "10px", flexDirection: "column" }}>

                            <Form.Item className="taskFormItem">
                                <label>Checkpoint Name</label>
                                <Input
                                    className="generic-input-control"
                                    placeholder="Enter checkpoint name"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                />
                            </Form.Item>

                            <Form.Item className="taskFormItem">
                                <label>Tag Type</label>
                                <Select
                                    className="custom-select"
                                    suffixIcon={<div className="dropdowncaret" />}
                                    placeholder="Select tag type"
                                    onChange={(val) => setTagType(val)}
                                    value={tagType}
                                    style={{ width: "100%" }}
                                >
                                    <Select.Option value="qr">QR Code</Select.Option>
                                    <Select.Option value="nfc">NFC Tag</Select.Option>
                                </Select>
                            </Form.Item>

                            <Form.Item className="taskFormItem">
                                <label style={{ marginBottom: "12px", display: "block" }}>
                                    Response Configuration
                                </label>
                                <p style={{ marginBottom: "16px", color: "#64748b", fontSize: "13px" }}>
                                    Choose how to capture the response when this checkpoint is scanned.
                                </p>

                                <div className="option-cards">
                                    <div
                                        className={responseFlow === "form" ? "option-card-selected option-card" : "option-card"}
                                        onClick={() => setResponseFlow("form")}
                                    >
                                        <span className="option-icon">📋</span>
                                        <div className="option-title">Use Existing Form</div>
                                        <div className="option-desc">
                                            Select an existing custom form template as the response
                                        </div>
                                    </div>

                                    <div
                                        className={responseFlow === "fields" ? "option-card-selected option-card" : "option-card"}
                                        onClick={() => setResponseFlow("fields")}
                                    >
                                        <span className="option-icon">➕</span>
                                        <div className="option-title">Create New Fields</div>
                                        <div className="option-desc">
                                            Build a custom set of fields from scratch for this checkpoint
                                        </div>
                                    </div>
                                </div>
                            </Form.Item>

                            {responseFlow === "form" && (
                                <Form.Item className="taskFormItem">
                                    <label>Select Form Template</label>
                                    <Select
                                        loading={getAllFormDataLoading}
                                        className="custom-select"
                                        suffixIcon={getAllFormDataLoading ? <Spinner /> : <CaretDownFilled />}
                                        placeholder={getAllFormDataLoading ? "Loading..." : "Select form template"}
                                        onChange={(val) => setFormId(val)}
                                        value={formId}
                                        style={{ width: "100%" }}
                                    >
                                        {getAllFormData?.getAllFormTemplate?.response?.map((form) => (
                                            <Select.Option key={form._id} value={form._id}>
                                                {form.templatename}
                                            </Select.Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            )}

                            {responseFlow === "fields" && (
                                <Form.Item className="taskFormItem">
                                    <label style={{ marginBottom: "8px", display: "block" }}>Response Fields</label>

                                    {fields.map((field, index) => (
                                        <div
                                            key={index}
                                            style={{
                                                borderTop: "3px dotted lightgray",
                                                paddingTop: "14px",
                                                marginBottom: "10px",
                                                position: "relative",
                                            }}
                                        >
                                            <div className="fieldNum">{index + 1}</div>

                                            <label>
                                                Label <small>if any?</small>
                                            </label>
                                            <Input
                                                className="generic-input-control mb16"
                                                placeholder="Field label"
                                                value={field.label}
                                                onChange={(e) => updateField(index, "label", e.target.value)}
                                                style={{ marginBottom: "10px" }}
                                            />

                                            <label>Field Type</label>
                                            <Select
                                                className="custom-select"
                                                suffixIcon={<div className="dropdowncaret" />}
                                                placeholder="Select field type"
                                                value={field.fieldType || undefined}
                                                onChange={(val) => updateField(index, "fieldType", val)}
                                                style={{ width: "100%", marginBottom: "10px" }}
                                            >
                                                {FIELD_TYPES.map((ft) => (
                                                    <Select.Option key={ft.value} value={ft.value}>
                                                        {ft.label}
                                                    </Select.Option>
                                                ))}
                                            </Select>

                                            <div style={{ display: "flex", justifyContent: "flex-end", gap: "8px" }}>
                                                {index === fields.length - 1 && (
                                                    <button className="sm-btn" type="button" onClick={addField}>
                                                        <FontAwesomeIcon icon={faPlus} /> &nbsp;Add another field
                                                    </button>
                                                )}
                                                {index > 0 && (
                                                    <button
                                                        className="sm-btn"
                                                        type="button"
                                                        onClick={() => removeField(index)}
                                                    >
                                                        <FontAwesomeIcon icon={faMinus} /> &nbsp;Remove
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </Form.Item>
                            )}
                        </Form>
                    )}
                </div>
            </React.Fragment>
        </Modal>
    );
};
